import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { SecondaryButton, IconButton } from "@/components/StandardButton";
import { useSidebar } from "@/components/ui/sidebar";

const navigationItems = [
  {
    id: "dashboard",
    title: "Dashboard",
    description: "View your feedback dashboard and recent activity",
    url: "/dashboard",
    keywords: ["home", "overview", "main"],
  },
  {
    id: "feedback",
    title: "Feedback",
    description: "Manage all feedback submissions",
    url: "/dashboard/feedback",
    keywords: ["submissions", "crm", "kanban", "manage"],
  },
  {
    id: "feedback-pages",
    title: "Feedback Links",
    description: "Create and manage feedback collection links",
    url: "/dashboard/pages",
    keywords: ["pages", "forms", "links", "create"],
  },
  {
    id: "analytics",
    title: "Analytics",
    description: "View detailed analytics and insights",
    url: "/dashboard/analytics",
    keywords: ["stats", "metrics", "data", "insights"],
  },
  {
    id: "create-page",
    title: "Create Page",
    description: "Create a new feedback page",
    url: "/dashboard/new",
    keywords: ["new", "add", "create", "form"],
  },
];

const settingsItems = [
  {
    id: "general-settings",
    title: "General Settings",
    description: "Update your company name and account information",
    url: "/dashboard/settings?tab=general",
    keywords: ["company", "account", "profile"],
  },
  {
    id: "branding-settings",
    title: "Branding Settings",
    description: "Customize your brand colors and logo",
    url: "/dashboard/settings?tab=branding",
    keywords: ["colors", "logo", "theme", "appearance"],
  },
  {
    id: "team-settings",
    title: "Team Settings",
    description: "Manage team members and invitations",
    url: "/dashboard/settings?tab=team",
    keywords: ["members", "invite", "users", "collaboration"],
  },
  {
    id: "notifications-settings",
    title: "Notification Settings",
    description: "Configure email alerts and notifications",
    url: "/dashboard/settings?tab=notifications",
    keywords: ["email", "alerts", "digest"],
  },
  {
    id: "billing-settings",
    title: "Billing Settings",
    description: "Manage your subscription and usage",
    url: "/dashboard/settings?tab=billing",
    keywords: ["plan", "subscription", "upgrade", "payment"],
  },
  {
    id: "privacy-settings",
    title: "Privacy Settings",
    description: "Data retention and account management",
    url: "/dashboard/settings?tab=privacy",
    keywords: ["data", "export", "delete", "gdpr"],
  },
];

export function SidebarSearch() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { open: sidebarOpen } = useSidebar();

  // Fetch user's feedback pages
  const { data: pages } = useQuery({
    queryKey: ["feedback-pages-search", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feedback_pages")
        .select("id, title, slug")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = (url: string) => {
    setOpen(false);
    navigate(url);
  };

  return (
    <>
      {sidebarOpen ? (
        <SecondaryButton
          className="w-full justify-start text-muted-foreground hover:text-foreground bg-background border-border"
          onClick={() => setOpen(true)}
        >
          <Search className="mr-2 h-4 w-4" strokeWidth={1.75} />
          <span className="flex-1 text-left">Search...</span>
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
            <span className="text-xs">⌘</span>K
          </kbd>
        </SecondaryButton>
      ) : (
        <IconButton
          className="w-full h-10 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-2xl transition-all duration-200"
          onClick={() => setOpen(true)}
          title="Search (⌘K)"
        >
          <Search className="h-5 w-5" strokeWidth={1.75} />
        </IconButton>
      )}

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search navigation, settings, and pages..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          
          <CommandGroup heading="Navigation">
            {navigationItems.map((item) => (
              <CommandItem
                key={item.id}
                onSelect={() => handleSelect(item.url)}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{item.title}</span>
                  <span className="text-sm text-muted-foreground">
                    {item.description}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandGroup heading="Settings">
            {settingsItems.map((item) => (
              <CommandItem
                key={item.id}
                onSelect={() => handleSelect(item.url)}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{item.title}</span>
                  <span className="text-sm text-muted-foreground">
                    {item.description}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>

          {pages && pages.length > 0 && (
            <CommandGroup heading="Your Pages">
              {pages.map((page) => (
                <CommandItem
                  key={page.id}
                  onSelect={() => handleSelect(`/dashboard/pages/${page.id}`)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{page.title}</span>
                    <span className="text-sm text-muted-foreground">
                      /{page.slug}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
